import { css } from '@emotion/react';
import { t } from 'i18n';
import { useState } from 'react';

import { LegacyCard } from '../LegacyCard';
import { SasIcon } from '../SasIcon';
import { theme } from '../theme';

type Props = {
  rows: React.ReactNode[];
  limit?: number;
  onEditDate?: () => void;
  linkHref?: string;
  linkText?: string;
};

const styles = {
  wrapper: css`
    width: 100%;
  `,
  rows: css`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.ms};

    @media (min-width: ${theme.breakpoints.mobileL}) {
      gap: ${theme.spacing.md};
    }
  `,
  pagination: css`
    margin-top: ${theme.spacing.md};
    justify-content: center;
    display: flex;
  `,
  arrow: (rotate: boolean) => css`
    margin-left: ${theme.spacing.xs};
    svg {
      transition: none;
      transform: rotate(${rotate ? '180deg' : '0deg'});
    }
  `,
  toggle: css`
    display: flex;
    align-items: center;
    cursor: pointer;
    font-size: ${theme.fontSize.sm};
    text-decoration: underline;
    color: ${theme.colors.link};

    &:hover {
      color: ${theme.colors.linkHover};
    }

    &:focus {
      color: ${theme.colors.link};
    }
  `,
  noFlights: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    > p {
      font-size: ${theme.fontSize.sm};
      margin: 0;
    }
  `,
};

export function FlightList({ rows, limit = 10, onEditDate, linkHref, linkText }: Props) {
  const [showHiddenRows, setShowHiddenRows] = useState(false);
  const displayRows = rows.slice(0, limit);
  const hiddenRows = rows.slice(limit);
  const hasHiddenRows = hiddenRows.length !== 0;

  if (rows.length === 0) {
    return (
      <LegacyCard>
        <div css={styles.noFlights}>
          <p>{t('error.noFlightsFound.message')}</p>
          {onEditDate && (
            <button className="btn-md btn-secondary" onClick={onEditDate}>
              {t('common:editDate')}
            </button>
          )}
          {linkHref && linkText && (
            <a href={linkHref} css={styles.toggle}>
              {linkText}
            </a>
          )}
        </div>
      </LegacyCard>
    );
  }

  return (
    <div css={styles.wrapper}>
      <div css={styles.rows}>
        {displayRows}
        {showHiddenRows && hiddenRows}
      </div>
      {hasHiddenRows && (
        <div css={styles.pagination}>
          <button css={styles.toggle} className="reset" onClick={() => setShowHiddenRows(!showHiddenRows)}>
            {showHiddenRows ? t('flightList.showLess') : t('flightList.showMore', { count: hiddenRows.length })}
            <span css={styles.arrow(showHiddenRows)}>
              <SasIcon name="in--arrow-down" />
            </span>
          </button>
        </div>
      )}
    </div>
  );
}
